import React, { useEffect, useState } from "react";
import api, { getErrorDetail, API_BASE } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Download, Upload } from "lucide-react";

const BANDS = [
  { key: "primary", en: "Primary (ages 6–9)", zh: "小学组 (6–9 岁)" },
  { key: "teens", en: "Teens (ages 10–13)", zh: "青少年组 (10–13 岁)" },
  { key: "k12", en: "K-12 (ages 14–17)", zh: "K-12 (14–17 岁)" },
  { key: "ib_alevel_cpe", en: "IB / A-Level / Cambridge", zh: "IB / A-Level / 剑桥" },
];

function PlannerRow({ band, info, onUploaded }) {
  const { lang } = useLang();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const label = lang === "zh" ? band.zh : band.en;

  async function upload() {
    if (!file) { toast.error("Pick a PDF file"); return; }
    if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
      toast.error("Only PDF files accepted"); return;
    }
    if (file.size > 20 * 1024 * 1024) { toast.error("PDF must be under 20 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/planners/${band.key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${band.en}`);
      setFile(null);
      const input = document.getElementById(`planner-file-${band.key}`);
      if (input) input.value = "";
      onUploaded(data);
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setBusy(false); }
  }

  const sizeKB = info?.exists ? (info.size_bytes / 1024).toFixed(0) : null;
  const looksPlaceholder = info?.exists && info.size_bytes < 30 * 1024; // current placeholders are ~12 KB

  return (
    <div data-testid={`planner-row-${band.key}`} className="rounded-2xl border border-[hsl(var(--border))] bg-white p-5 md:p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))]">{band.key}</p>
          <h4 className="font-display text-lg tracking-tight mt-1">{label}</h4>
          <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
            {info?.exists ? (
              <>
                <span data-testid={`planner-size-${band.key}`}>{sizeKB} KB</span>
                {info.updated_at && (
                  <> · <span data-testid={`planner-updated-${band.key}`}>updated {format(parseISO(info.updated_at), "MMM dd HH:mm")}</span></>
                )}
                {looksPlaceholder && (
                  <span data-testid={`planner-placeholder-${band.key}`} className="ml-2 text-[hsl(var(--destructive))]">· placeholder (replace with real PDF)</span>
                )}
              </>
            ) : (
              <span data-testid={`planner-missing-${band.key}`} className="text-[hsl(var(--destructive))]">no file uploaded yet</span>
            )}
          </div>
        </div>
        {info?.exists && (
          <a
            href={`${API_BASE}/planner/${band.key}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`planner-preview-${band.key}`}
            className="text-xs px-3 py-1.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] flex items-center gap-1.5 whitespace-nowrap"
          ><Download className="w-3.5 h-3.5" />Preview current</a>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-12 gap-3 items-center">
        <input
          id={`planner-file-${band.key}`}
          type="file"
          accept="application/pdf"
          onChange={(e)=>setFile(e.target.files?.[0] || null)}
          data-testid={`planner-file-${band.key}`}
          className="sm:col-span-8 block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[hsl(var(--muted))] file:px-4 file:py-2 file:text-xs file:tracking-[0.15em] file:uppercase file:text-[hsl(var(--foreground))] hover:file:bg-[hsl(var(--muted))]/70"
        />
        <Button
          onClick={upload}
          disabled={busy || !file}
          data-testid={`planner-upload-${band.key}`}
          className="sm:col-span-4 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"
        >
          <Upload className="w-3.5 h-3.5 mr-1.5" />
          {busy ? "Uploading…" : info?.exists ? "Replace PDF" : "Upload PDF"}
        </Button>
        {file && <p className="sm:col-span-12 text-xs text-[hsl(var(--muted-foreground))]">{file.name} · {(file.size/1024).toFixed(0)} KB</p>}
      </div>
    </div>
  );
}

export default function PlannersPanel() {
  const [list, setList] = useState([]);

  async function load() {
    try { const { data } = await api.get("/admin/planners"); setList(data); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  function handleUploaded(updated) {
    setList((prev) => prev.map((p) => p.age_band === updated.age_band ? updated : p));
  }

  const byBand = Object.fromEntries(list.map((p) => [p.age_band, p]));

  return (
    <div data-testid="admin-planners-panel" className="space-y-4">
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 p-4 text-xs text-[hsl(var(--muted-foreground))]">
        These are the four printable PDFs delivered to Reading Challenge sign-ups by age band. Replace any placeholder PDF with the real 30-day plan — parents see the new file the next time they hit the Download link.
      </div>
      {BANDS.map((b) => (
        <PlannerRow key={b.key} band={b} info={byBand[b.key]} onUploaded={handleUploaded} />
      ))}
    </div>
  );
}
